import { Component, computed, DestroyRef, effect, ElementRef, inject, input, OnInit, output, signal, untracked, ViewChild } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { GoogleMap, GoogleMapsModule } from '@angular/google-maps'
import { GooglePlacesService } from '@app/v2/event/services/google-places.service'
import { Checkin, CheckinLocationDetails, CheckinLocationPatchPayload } from '@app/v2/event/types/event.type'
import { LocationChangeSource, LocationResult, LocationState } from '@app/v2/event/types/location-timezone.type'
import { DropdownComponent } from '@app/v2/shared/components/dropdown/dropdown.component'
import { DropdownOption } from '@app/v2/shared/components/dropdown/types/dropdown-option.type'
import { BasicInputComponent } from '@app/v2/shared/components/inputs/basic-input/basic-input.component'
import { SavingIndicatorComponent } from '@app/v2/shared/components/saving-indicator/saving-indicator.component'
import { BasicInputMode } from '@app/v2/shared/enums/input-modes.enum'
import { DateTime } from 'luxon'
import { NgxTippyModule } from 'ngx-tippy-wrapper'
import { of, Subject } from 'rxjs'
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators'

@Component({
  selector: 'app-location-time-zone',
  imports: [NgxTippyModule, DropdownComponent, ReactiveFormsModule, GoogleMapsModule, BasicInputComponent, SavingIndicatorComponent],
  providers: [GooglePlacesService],
  templateUrl: './location-time-zone.component.html',
  styleUrl: './location-time-zone.component.scss'
})
export class LocationTimeZoneComponent implements OnInit {
  @ViewChild('locationDropdown') private _locationDropdown?: DropdownComponent
  @ViewChild('locationSearchInput') private _locationSearchInput?: ElementRef<HTMLInputElement>
  @ViewChild('eventMap') private _eventMap?: GoogleMap

  checkin = input<Checkin | null>(null)
  organizationId = input<number | null>(null)
  eventId = input<number | null>(null)
  eventTimezoneOffsetString = input<string | null>(null)
  isSaving = input<boolean>(false)
  locationDetailsChange = output<CheckinLocationPatchPayload>()

  public eventLocationForm = new FormGroup({
    venue_name: new FormControl<string>('', [Validators.required]),
    venue_address: new FormControl<string>('', [Validators.required])
  })

  public BasicInputMode = BasicInputMode
  public showSavedIndicator = signal(false)
  public locationControl = new FormControl('')
  public readonly locationTooltipContent = `How to set your event location<br/><br/>1- Search for and select your venue.<br/>2- Adjust the red pin to fine-tune the location, if needed.<br/>3- Edit the venue name and address if the auto-generated details are unclear or inaccurate.<br/><br/>* For virtual events: search for a city to set the event time zone. You can ignore the remaining steps after that.`
  public locationOptions = signal<DropdownOption[]>([])
  public hasSearchQuery = signal(false)
  public readonly markerOptions: google.maps.MarkerOptions = {
    draggable: true
  }

  public readonly mapOptions: google.maps.MapOptions = {
    gestureHandling: 'cooperative',
    mapTypeControl: false,
    mapTypeId: google.maps.MapTypeId.ROADMAP,
    streetViewControl: false
  }

  public mapState = signal<{
    center: google.maps.LatLngLiteral | null
    marker: google.maps.LatLngLiteral | null
    zoom: number
  }>({
    center: null,
    marker: null,
    zoom: LocationTimeZoneComponent.DEFAULT_MAP_ZOOM
  })
  public readonly selectedCity = signal<string>('')
  public readonly selectedTimezoneId = signal<string>('')
  /** Locally fetched offset in hours; takes precedence over the parent input until checkin re-syncs. */
  public readonly selectedTimezoneOffsetHours = signal<number | null>(null)
  public readonly selectedTimezoneLabel = computed<string>(() => {
    if (!this.selectedTimezoneId()) return ''
    const nowInSelectedTimezone = DateTime.now().setZone(this.selectedTimezoneId())
    if (!nowInSelectedTimezone.isValid) return ''
    const parsed = nowInSelectedTimezone.offset / 60
    return this._formatGmtLabel(parsed)
  })
  public readonly selectedVenueCountry = signal<string>('')
  public isLocationDropdownOpen = false
  public readonly locationState = signal<LocationState | null>(null)
  private static readonly COORD_EPSILON = 1e-6
  private static readonly DEFAULT_MAP_ZOOM = 17
  private static readonly ZOOM_SAVE_DEBOUNCE_MS = 400
  private static readonly LOCATION_SEARCH_DEBOUNCE_MS = 300

  private readonly _zoomChanged$ = new Subject<number>()
  private readonly _googlePlacesService = inject(GooglePlacesService)
  private readonly _destroyRef = inject(DestroyRef)
  private _previousIsSaving = signal(false)
  private _blurSaveTimeout: ReturnType<typeof setTimeout> | null = null
  private _pendingBlurSave: { field: keyof CheckinLocationDetails; value: string } | null = null
  private _cancelNextBlurSave = false
  private readonly _blurSaveDelayMs = 150

  constructor() {
    effect(() => this._syncFromCheckin())
    effect(() => this._applyLocationStateAndTimezone)
    effect(() => this._updateSavingIndicator())
  }

  ngOnInit(): void {
    this._bindZoomDebouncedSave()
    this._bindLocationSearchPredictions()
  }

  /**
   * @description Syncs the form, city label, and map from checkin input when it changes
   * @returns void
   */
  private _syncFromCheckin(): void {
    const details = this.checkin()?.location_details
    if (details?.venue_name !== undefined) this.eventLocationForm.get('venue_name')?.setValue(details.venue_name ?? '')
    if (details?.venue_address !== undefined) this.eventLocationForm.get('venue_address')?.setValue(details.venue_address ?? '')
    if (details?.venue_city) this.selectedCity.set(details.venue_city)
    this.selectedTimezoneId.set(details?.timezone ?? '')
    this.selectedVenueCountry.set(details?.venue_country ?? '')
    const coords = LocationTimeZoneComponent._pinCoordsFromDetails(details)
    if (!coords) return
    untracked(() => {
      const marker = this.mapState().marker
      if (marker && !LocationTimeZoneComponent._latLngBeyondEpsilon(coords, marker)) return
      const zoom = this._parseSavedZoom(details?.map_zoom_level)
      this.mapState.update((c) => ({ ...c, center: coords, marker: coords, zoom }))
    })
  }

  private static _pinCoordsFromDetails(details: CheckinLocationDetails | undefined): google.maps.LatLngLiteral | null {
    const lat = details?.pin_latitude ?? details?.map_latitude
    const lng = details?.pin_longitude ?? details?.map_longitude
    if (lat == null || lng == null) return null
    return { lat, lng }
  }

  private static _latLngBeyondEpsilon(a: google.maps.LatLngLiteral, b: google.maps.LatLngLiteral): boolean {
    return Math.abs(a.lat - b.lat) > LocationTimeZoneComponent.COORD_EPSILON || Math.abs(a.lng - b.lng) > LocationTimeZoneComponent.COORD_EPSILON
  }

  private _applyLocationStateAndTimezone(): void {
    const state = this.locationState()
    if (!state) return
    const coords = { lat: state.lat, lng: state.lng }
    untracked(() => {
      this.mapState.update((current) => ({ ...current, center: coords, marker: coords, zoom: LocationTimeZoneComponent.DEFAULT_MAP_ZOOM }))
    })
  }

  private _updateSavingIndicator(): void {
    const saving = this.isSaving()
    const wasSaving = this._previousIsSaving()
    this._previousIsSaving.set(saving)
    if (wasSaving && !saving) this.showSavedIndicator.set(true)
  }

  public onSavedIndicatorDone(): void {
    this.showSavedIndicator.set(false)
  }

  /**
   * Binds the zoom changed event to the debounced save
   */
  private _bindZoomDebouncedSave(): void {
    this._zoomChanged$
      .pipe(debounceTime(LocationTimeZoneComponent.ZOOM_SAVE_DEBOUNCE_MS), distinctUntilChanged(), takeUntilDestroyed(this._destroyRef))
      .subscribe((zoom) => this._emitZoomUpdateIfChanged(zoom))
  }

  private _bindLocationSearchPredictions(): void {
    this.locationControl.valueChanges
      .pipe(
        debounceTime(LocationTimeZoneComponent.LOCATION_SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(),
        switchMap((term: string | null) => {
          const value = term?.trim() ?? ''
          this.hasSearchQuery.set(!!value)
          if (!value) {
            this.locationOptions.set([])
            return of<LocationResult[]>([])
          }
          return this._googlePlacesService.getGeocodePredictions$(value)
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((results) => {
        if (!results.length) {
          this.locationOptions.set([])
          return
        }
        this.locationOptions.set(
          results.map((location) => ({
            key: location.id,
            value: location.label,
            data: location
          }))
        )
        this._openDropdownIfNeeded()
      })
  }

  /**
   * @description Handles the selection of a location option from the dropdown
   * @param {DropdownOption} option - The selected location option
   * @returns void
   */
  public onLocationOptionClicked(option: DropdownOption): void {
    this.locationControl.setValue(option.value, { emitEvent: false })
    this.locationOptions.set([])
    this.hasSearchQuery.set(false)

    const location = option.data as LocationResult
    const placeId = location?.id

    if (!placeId) {
      return
    }

    this._googlePlacesService
      .getPlaceDetails$(placeId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((result) => {
        if (!result) return
        this.selectedTimezoneId.set(result.timeZone ?? '')
        const newCenter = { lat: result.lat, lng: result.lng }
        this._updateLocationUI(result, true)
        this._setLocationState(newCenter, 'search')
        this._emitLocationDetails(newCenter, true)
      })
  }

  /**
   * @description Handles marker drag and updates location context
   * @param {google.maps.MapMouseEvent} event - Marker drag end event
   * @returns void
   */
  public onMarkerDragEnd(event: google.maps.MapMouseEvent): void {
    const draggedPosition = event.latLng?.toJSON()
    if (!draggedPosition) return
    this._applyManualLocation(draggedPosition)
  }

  /**
   * @description Updates the location UI
   * @param {LocationResult} result - The selected location result
   * @param {boolean} overwriteVenueFields - Whether to overwrite venue fields even if user has edited them
   * @returns void
   */
  private _updateLocationUI(result: LocationResult, overwriteVenueFields: boolean): void {
    this._applyVenueFields(result, overwriteVenueFields)
    let city = result.cityLabel || result.label
    if (['dubai', 'دبي'].includes(city.toLowerCase())) {
      city = 'Dubai'
    } else if (['abu dhabi', 'أبو ظبي'].includes(city.toLowerCase())) {
      city = 'Abu Dhabi'
    }
    this.selectedCity.set(city)
    if (result.venueCountry !== undefined) this.selectedVenueCountry.set(result.venueCountry)
  }

  /**
   * @description Applies the venue fields to the form
   * @param {LocationResult} result - The selected location result
   * @param {boolean} overwrite - Whether to overwrite the venue fields
   * @returns void
   */
  private _applyVenueFields(result: LocationResult, overwrite: boolean): void {
    const venueNameControl = this.eventLocationForm.get('venue_name')
    const venueAddressControl = this.eventLocationForm.get('venue_address')

    if (result.venueName && (overwrite || this._isControlEmpty(venueNameControl?.value))) {
      venueNameControl?.setValue(result.venueName)
    }

    if (result.venueAddress && (overwrite || this._isControlEmpty(venueAddressControl?.value))) {
      venueAddressControl?.setValue(result.venueAddress)
    }
  }

  /*
   * @description Checks if the control is empty
   * @param {string | null | undefined} value - The value of the control
   * @returns {boolean} Whether the control is empty
   */
  private _isControlEmpty(value: string | null | undefined): boolean {
    return !value?.trim()
  }

  /**
   * @description Checks if the venue fields are required
   * @returns {boolean} Whether the venue fields are required
   */
  private _hasRequiredVenueFields(): boolean {
    return !this._isControlEmpty(this.eventLocationForm.get('venue_name')?.value) && !this._isControlEmpty(this.eventLocationForm.get('venue_address')?.value)
  }

  /**
   * @description Sets the location state
   * @param {google.maps.LatLngLiteral} coords - The coordinates
   * @param {LocationChangeSource} source - The source of the location change
   * @returns void
   */
  private _setLocationState(coords: google.maps.LatLngLiteral, source: LocationChangeSource): void {
    this.locationState.set({
      lat: coords.lat,
      lng: coords.lng,
      source
    })
  }

  /**
   * @description Formats a numeric hour offset into a "(GMT±H)" label.
   * @param {number} hours - The offset in hours.
   * @returns {string} The formatted label.
   */
  private _formatGmtLabel(hours: number): string {
    return `(GMT${hours > 0 ? '+' : ''}${hours})`
  }

  /**
   * Build a full location_details payload from saved details + current UI state.
   * @param {google.maps.LatLngLiteral} coords - The coordinates
   * @returns {Partial<CheckinLocationDetails>} The full location details
   */
  private _buildFullLocationDetails(coords: google.maps.LatLngLiteral): Partial<CheckinLocationDetails> {
    const saved = this.checkin()?.location_details ?? {}
    const venueName = String(this.eventLocationForm.get('venue_name')?.value ?? '').trim()
    const venueAddress = String(this.eventLocationForm.get('venue_address')?.value ?? '').trim()
    return {
      ...saved,
      pin_latitude: coords.lat,
      pin_longitude: coords.lng,
      map_latitude: coords.lat,
      map_longitude: coords.lng,
      map_zoom_level: this.mapState().zoom,
      venue_latitude: String(coords.lat),
      venue_longitude: String(coords.lng),
      venue_name: venueName,
      venue_address: venueAddress,
      venue_city: this.selectedCity() || saved.venue_city || undefined,
      venue_country: this.selectedVenueCountry() || saved.venue_country || undefined,
      timezone: this.selectedTimezoneId() || saved.timezone || undefined
    }
  }

  /**
   * Parses saved map zoom from API; falls back to default when missing or invalid.
   */
  private _parseSavedZoom(raw: number | string | null | undefined): number {
    if (raw === undefined || raw === null || String(raw).trim() === '') return LocationTimeZoneComponent.DEFAULT_MAP_ZOOM
    const n = Number(raw)
    return Number.isFinite(n) && n > 0 ? n : LocationTimeZoneComponent.DEFAULT_MAP_ZOOM
  }

  /**
   * Reads current zoom from the Google Map instance and pushes it to the debounced update stream.
   */
  public onMapZoomChanged(): void {
    const raw = this._eventMap?.googleMap?.getZoom()
    if (raw == null) return
    const zoom = Math.round(raw)
    this.mapState.update((s) => ({ ...s, zoom }))
    if (!this._hasRequiredVenueFields()) return
    this._zoomChanged$.next(zoom)
  }

  /**
   * Persists zoom when it differs from checkin.location_details (via parent updateCheckinEvent).
   */
  private _emitZoomUpdateIfChanged(zoom: number): void {
    const marker = this.mapState().marker
    if (!this.checkin() || !marker) return
    if (!this._hasRequiredVenueFields()) return
    const saved = this._parseSavedZoom(this.checkin().location_details?.map_zoom_level)
    if (zoom === saved) return
    this.locationDetailsChange.emit({ checkinId: this.checkin().id, location_details: this._buildFullLocationDetails(marker) })
  }
  /**
   * Emits location details only if something actually changed.
   */
  private _emitLocationDetails(coords: google.maps.LatLngLiteral, includeVenueFields = false): void {
    if (!this.checkin()) return
    const locationDetails = this.checkin()?.location_details
    const venueName = String(this.eventLocationForm.get('venue_name')?.value ?? '').trim()
    const venueAddress = String(this.eventLocationForm.get('venue_address')?.value ?? '').trim()
    const savedLat = locationDetails?.pin_latitude ?? locationDetails?.map_latitude
    const savedLng = locationDetails?.pin_longitude ?? locationDetails?.map_longitude
    const coordsChanged = savedLat == null || savedLng == null || LocationTimeZoneComponent._latLngBeyondEpsilon(coords, { lat: savedLat, lng: savedLng })
    if (!coordsChanged && !includeVenueFields) return
    const details = this._buildFullLocationDetails(coords)
    if (includeVenueFields) {
      if (!coordsChanged && venueName === String(locationDetails?.venue_name ?? '').trim() && venueAddress === String(locationDetails?.venue_address ?? '').trim()) return
    }
    this.locationDetailsChange.emit({ checkinId: this.checkin()?.id, location_details: details })
  }

  /**
   * On Enter, blur so scheduleBlurSave runs.
   * @param {KeyboardEvent} event - The keyboard event
   * @returns void
   */
  public handleLocationFieldKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault()
      ;(event.target as HTMLElement)?.blur()
    }
  }

  /**
   * Blur handler for venue fields. Schedules save only if value actually changed.
   */
  public handleLocationFieldBlur(field: keyof CheckinLocationDetails): void {
    const control = this.eventLocationForm.get(field)
    if (!control?.valid) return
    const value = (control.value ?? '').toString().trim()
    const saved = String(this.checkin()?.location_details?.[field] ?? '').trim()
    if (value === saved) return
    this._scheduleBlurSave(field, value)
  }

  /**
   * True if form or marker differs from checkin. Don't treat as unsaved while saving.
   * @returns {boolean} Whether the form or marker differs from the checkin
   */
  public readonly hasUnsavedChanges = computed(() => {
    if (this.isSaving() || !this.checkin()) return false
    const marker = this.mapState().marker
    if (!marker) return false
    const locationDetails = this.checkin()?.location_details
    const venueName = String(this.eventLocationForm.get('venue_name')?.value ?? '').trim()
    const venueAddress = String(this.eventLocationForm.get('venue_address')?.value ?? '').trim()
    const timezone = String(this.selectedTimezoneId() ?? '')
    const venueCountry = String(this.selectedVenueCountry() ?? '')
    if (venueName !== String(locationDetails?.venue_name ?? '').trim()) return true
    if (venueAddress !== String(locationDetails?.venue_address ?? '').trim()) return true
    if (timezone !== String(locationDetails?.timezone ?? '')) return true
    if (venueCountry !== String(locationDetails?.venue_country ?? '')) return true

    const savedLat = locationDetails?.pin_latitude ?? locationDetails?.map_latitude
    const savedLng = locationDetails?.pin_longitude ?? locationDetails?.map_longitude

    if (savedLat != null && savedLng != null) {
      if (LocationTimeZoneComponent._latLngBeyondEpsilon(marker, { lat: savedLat, lng: savedLng })) return true
    }

    if (this.mapState().zoom !== this._parseSavedZoom(locationDetails?.map_zoom_level)) return true

    return false
  })

  /** Build payload for modal save; returns null if nothing changed or required fields are invalid. */
  public getUnsavedChanges(): CheckinLocationPatchPayload | null {
    this.cancelPendingBlurSave()
    const marker = this.mapState().marker
    if (!this.checkin() || !marker) return null
    const venueNameControl = this.eventLocationForm.get('venue_name')
    const venueAddressControl = this.eventLocationForm.get('venue_address')
    // If required venue fields are invalid, treat save-from-modal as discard for this section.
    if (venueNameControl?.invalid || venueAddressControl?.invalid) return null

    const details: Partial<CheckinLocationDetails> = {}
    const venueName = String(venueNameControl?.value ?? '').trim()
    const venueAddress = String(venueAddressControl?.value ?? '').trim()
    const timezone = String(this.selectedTimezoneId() ?? '')
    const venueCountry = String(this.selectedVenueCountry() ?? '')
    const locationDetails = this.checkin()?.location_details
    if (venueName !== String(locationDetails?.venue_name ?? '').trim()) details.venue_name = venueName
    if (venueAddress !== String(locationDetails?.venue_address ?? '').trim()) details.venue_address = venueAddress
    if (timezone !== String(locationDetails?.timezone ?? '')) details.timezone = timezone
    if (venueCountry !== String(locationDetails?.venue_country ?? '')) details.venue_country = venueCountry
    const savedLat = locationDetails?.pin_latitude ?? locationDetails?.map_latitude
    const savedLng = locationDetails?.pin_longitude ?? locationDetails?.map_longitude
    if (savedLat == null || savedLng == null || LocationTimeZoneComponent._latLngBeyondEpsilon(marker, { lat: savedLat, lng: savedLng })) {
      details.pin_latitude = marker.lat
      details.pin_longitude = marker.lng
      details.map_latitude = marker.lat
      details.map_longitude = marker.lng
      details.venue_latitude = String(marker.lat)
      details.venue_longitude = String(marker.lng)
    }
    if (this.mapState().zoom !== this._parseSavedZoom(locationDetails?.map_zoom_level)) details.map_zoom_level = this.mapState().zoom
    if (Object.keys(details).length === 0) return null
    return { checkinId: this.checkin()?.id, location_details: this._buildFullLocationDetails(marker) }
  }

  /**
   * Cancels the pending blur save
   * @returns void
   */
  public cancelPendingBlurSave(): void {
    this._cancelNextBlurSave = true
    this._pendingBlurSave = null
    if (this._blurSaveTimeout !== null) {
      clearTimeout(this._blurSaveTimeout)
      this._blurSaveTimeout = null
    }
  }

  /**
   * Schedules the blur save
   * @param {keyof CheckinLocationDetails} field - The field to save
   * @param {string} value - The value to save
   * @returns void
   */
  private _scheduleBlurSave(field: keyof CheckinLocationDetails, value: string): void {
    if (this._blurSaveTimeout !== null) clearTimeout(this._blurSaveTimeout)
    this._pendingBlurSave = { field, value }
    this._blurSaveTimeout = setTimeout(() => {
      this._blurSaveTimeout = null
      const pending = this._pendingBlurSave
      this._pendingBlurSave = null
      if (this._cancelNextBlurSave) {
        this._cancelNextBlurSave = false
        return
      }
      if (!pending) return
      if (!this.checkin()) return
      const marker = this.mapState().marker
      if (!marker) return
      const control = this.eventLocationForm.get(pending.field)
      if (!control?.valid) return
      const saved = String(this.checkin()?.location_details?.[pending.field] ?? '').trim()
      if (pending.value === saved) return
      this.locationDetailsChange.emit({ checkinId: this.checkin()!.id, location_details: this._buildFullLocationDetails(marker) })
    }, this._blurSaveDelayMs)
  }

  /**
   * @description Opens the dropdown if needed
   * @returns void
   */
  private _openDropdownIfNeeded(): void {
    if (this.isLocationDropdownOpen) return
    if (!this.hasSearchQuery()) return
    this._locationDropdown?.toggleDropdown(new globalThis.Event('click'))
  }

  /**
   * @description Sets the current location
   * @returns void
   */
  public setCurrentLocation(): void {
    if (!navigator.geolocation) return

    this._locationSearchInput?.nativeElement.blur()

    // Ensure search dropdown is closed so body scroll is not locked by its overlay.
    this.hasSearchQuery.set(false)
    this.locationOptions.set([])
    if (this.isLocationDropdownOpen) this._locationDropdown?.toggleDropdown(new globalThis.Event('click'))

    navigator.geolocation.getCurrentPosition(({ coords }) => {
      this._applyManualLocation(
        {
          lat: coords.latitude,
          lng: coords.longitude
        },
        true
      )
    })
    this.locationControl.setValue('')
  }

  /**
   * @description Applies the manual location
   * @param {google.maps.LatLngLiteral} coords - The coordinates
   * @param {boolean} overwriteVenueFields - Whether to overwrite venue fields with reverse geocode result
   * @returns void
   */
  private _applyManualLocation(coords: google.maps.LatLngLiteral, overwriteVenueFields = false): void {
    this._googlePlacesService
      .reverseGeocode$(coords)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((result) => {
        if (result) {
          this._updateLocationUI(result, overwriteVenueFields)
          this.selectedTimezoneId.set(result.timeZone ?? '')
        }
        this._setLocationState(coords, 'drag')
        this._emitLocationDetails(coords, true)
      })
  }
}
